import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import axios from 'axios';
import { getValidationFailures } from '../../services/licenseService';

const ValidationFailuresList = () => {
  const [loading, setLoading] = useState(true);
  const [failures, setFailures] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    licenseKey: '',
    startDate: '',
    endDate: '',
    skip: 0,
    limit: 10,
  });

  const fetchValidationFailures = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.licenseKey) params.append('license_key', filters.licenseKey);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      params.append('skip', filters.skip);
      params.append('limit', filters.limit);

      const response = await getValidationFailures(params);
      
      // const response = await axios.get(`/api/v1/licenses/validation-failures?${params.toString()}`, {
      //   headers: {
      //     'Accept': 'application/json',
      //     'Content-Type': 'application/json'
      //   },
      //   withCredentials: true
      // });
      
      setFailures(response.items || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching validation failures:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to load validation failures';
      toast.error(errorMessage);
      setFailures([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    fetchValidationFailures();
    
    return () => {
      controller.abort();
    };
  }, [filters.skip, filters.limit, filters.startDate, filters.endDate]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      skip: 0, // Reset to first page when filters change
    }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    // Reset to first page when filters change
    setFilters(prev => ({
      ...prev,
      skip: 0
    }));
  };
  
  const clearFilters = () => {
    setFilters({
      licenseKey: '',
      startDate: '',
      endDate: '',
      skip: 0,
      limit: 10,
    });
  };
  
  const isFilterActive = filters.licenseKey || filters.startDate || filters.endDate;

  const handlePageChange = (newSkip) => {
    setFilters(prev => ({
      ...prev,
      skip: newSkip,
    }));
    // Scroll to top of the table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss');
    } catch (e) {
      return dateString || 'N/A';
    }
  };

  const totalPages = Math.ceil(total / filters.limit);
  const currentPage = Math.floor(filters.skip / filters.limit) + 1;

  return (
    <Container fluid className="py-4">
      {/* Filters */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Filters</h5>
          {isFilterActive && (
            <Button 
              variant="link" 
              size="sm" 
              onClick={clearFilters}
              className="text-decoration-none"
            >
              <i className="bi bi-x-circle me-1"></i> Clear Filters
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleFilterSubmit}>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group controlId="licenseKey">
                  <Form.Label>License Key</Form.Label>
                  <Form.Control
                    type="text"
                    name="licenseKey"
                    value={filters.licenseKey}
                    onChange={handleFilterChange}
                    placeholder="Enter license key"
                    autoComplete="off"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="startDate">
                  <Form.Label>From Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                    max={filters.endDate || new Date().toISOString().split('T')[0]}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="endDate">
                  <Form.Label>To Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                    min={filters.startDate}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </Form.Group>
              </Col>
              {/* <Col xs={12} md={2} className="d-flex align-items-end">
                <Button 
                  variant="primary" 
                  type="submit" 
                  className="w-100 d-flex align-items-center justify-content-center"
                  disabled={loading}
                  style={{
                    minHeight: '38px',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner 
                        as="span" 
                        size="sm" 
                        animation="border" 
                        role="status" 
                        className="me-2" 
                        aria-hidden="true"
                      />
                      <span>Applying...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-funnel-fill me-2"></i>
                      <span>Apply Filters</span>
                    </>
                  )}
                </Button>
              </Col> */}
            </Row>
            {isFilterActive && (
              <div className="mt-2 small text-muted">
                <i className="bi bi-info-circle me-1"></i>
                Showing filtered results. 
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={clearFilters}
                  className="p-0 ms-1 align-baseline"
                >
                  Clear all filters
                </Button>
              </div>
            )}
          </Form>
        </Card.Body>
      </Card>

      {/* Results */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-2">Loading validation failures...</p>
            </div>
          ) : failures.length === 0 ? (
            <div className="text-center py-5">
              <p>No validation failures found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>License Key</th>
                      <th>Error Type</th>
                      <th>Error Message</th>
                      <th>IP Address</th>
                      <th>Client Version</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failures.map((failure) => (
                      <tr key={failure.id}>
                        <td>{formatDate(failure.timestamp)}</td>
                        <td>
                          <code>{failure.license_key || 'N/A'}</code>
                        </td>
                        <td>
                          <span className="badge bg-danger">
                            {failure.error_type || 'Unknown'}
                          </span>
                        </td>
                        <td>
                          <div className="text-danger">
                            {failure.error_message}
                            {failure.error_details && Object.keys(failure.error_details).length > 0 && (
                              <div className="small text-muted mt-1">
                                {JSON.stringify(failure.error_details)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{failure.ip_address || 'N/A'}</td>
                        <td>{failure.client_version || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {total > filters.limit && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {filters.skip + 1} to {Math.min(
                      filters.skip + filters.limit,
                      total
                    )} of {total} entries
                  </div>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={filters.skip === 0}
                      onClick={() => handlePageChange(Math.max(0, filters.skip - filters.limit))}
                      className="me-2"
                    >
                      Previous
                    </Button>
                    <span className="mx-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={filters.skip + filters.limit >= total}
                      onClick={() => handlePageChange(filters.skip + filters.limit)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ValidationFailuresList;
